/**
 * @file billingPreauth.ts
 * @brief Boardroom card gate + hold — web parity for mobile
 *        `preauthorizeProduct('boardroom')`.
 *
 * @author DES
 * @date 28-07-2026
 */

import { z } from "zod";
import { ApiClientError, apiGet, apiPost } from "@/shared/api/client";
import { billingEndpoints } from "@/shared/api/endpoints";
import { authRequiredDetails, confirmWithBank } from "@/shared/stripe/confirmWithBank";
import { billingErrorDetails, type BillingErrorDetails } from "@/features/my-billing/billingErrors";

const PreauthSuccessSchema = z.object({
  success: z.boolean().optional(),
  paymentIntentId: z.string().optional(),
  authorizedCents: z.number().optional(),
  currency: z.string().optional(),
  skipped: z.boolean().optional(),
  /** Free plan start: no card, no hold (B4, 22-10-2026). */
  freePlan: z.boolean().optional(),
  freeMinutesRemaining: z.number().optional(),
  /** A plan covers Meeting Notes: no hold, same path as Free (B6, 22-10-2026). */
  planCovered: z.boolean().optional(),
  /**
   * B43 (31-10-2026): a Free live recording warns at `warnAfterSeconds` and
   * stops by itself at `stopAfterSeconds` (free minutes + the grace).
   */
  limitMinutes: z.number().optional(),
  graceMinutes: z.number().optional(),
  warnAfterSeconds: z.number().optional(),
  stopAfterSeconds: z.number().optional(),
  warningMessage: z.string().optional(),
});

/** B43: when a Free live recording warns and stops (absent = never cut). */
export type FreeRecordingWindow = {
  warnAfterSeconds: number;
  stopAfterSeconds: number;
  warningMessage: string;
};

export type PreauthResult =
  | {
      ok: true;
      paymentIntentId?: string;
      authorizedCents?: number;
      currency?: string;
      skipped?: boolean;
      /** B43: Free only. */
      freeWindow?: FreeRecordingWindow | null;
    }
  | { ok: false; error: string; message?: string; details?: BillingErrorDetails };

/**
 * Place a Stripe hold (or skip when Stripe disabled) before recording.
 * On MISSING_PAYMENT_METHOD the caller must block capture.
 *
 * Backend may omit `success` on some paths; treat skipped / PI as ok.
 *
 * `tier` (B4, 22-10-2026): a Meeting Notes recording says so, so a Free user
 * starts with no card and no hold (or gets 402 `FREE_MINUTES_USED` (B43; was
 * `FREE_LIMIT_REACHED`) / `FREE_INTEL_LIMIT` / 403
 * `EMAIL_NOT_VERIFIED`); without it the server treats it as Full and asks
 * for a card.
 *
 * B6 (22-10-2026): Pro / Business / seats / Enterprise get `skipped: true,
 * planCovered: true` for Notes (no hold, nothing to release), or 402
 * `FAIR_USE_REACHED` past the month's fair use. A Full recording always asks
 * for its own hold: never reuse a Notes `skipped` answer for Full.
 */
export async function preauthorizeBoardroom(tier: "full" | "notes" = "full"): Promise<PreauthResult> {
  const first = await requestHold(undefined, tier);
  if (first.ok || first.error !== "AUTH_REQUIRED" || !first.challenge) return first;
  // 3DS (18-10-2026): the bank's challenge in the page, then the same call
  // naming the confirmed PaymentIntent so the server adopts that hold.
  const bank = await confirmWithBank(first.challenge.clientSecret, first.challenge.paymentIntentId);
  if (!bank.ok) return { ok: false, error: bank.code, message: bank.message };
  const second = await requestHold(bank.paymentIntentId, tier);
  return second.ok ? second : { ok: false, error: second.error, message: second.message, details: second.details };
}

type HoldAttempt =
  | Extract<PreauthResult, { ok: true }>
  | {
      ok: false;
      error: string;
      message?: string;
      details?: BillingErrorDetails;
      challenge?: { clientSecret: string; paymentIntentId: string } | null;
    };

async function requestHold(paymentIntentId: string | undefined, tier: "full" | "notes"): Promise<HoldAttempt> {
  try {
    const raw = await apiPost<unknown>(billingEndpoints.preauthorize, {
      kind: "boardroom",
      ...(tier === "notes" ? { tier: "notes" } : {}),
      ...(paymentIntentId ? { paymentIntentId } : {}),
    });
    const data = PreauthSuccessSchema.parse(raw);
    return {
      ok: true,
      paymentIntentId: data.paymentIntentId,
      authorizedCents: data.authorizedCents,
      currency: data.currency,
      skipped: data.skipped,
      freeWindow:
        data.freePlan && typeof data.stopAfterSeconds === "number"
          ? {
              warnAfterSeconds: data.warnAfterSeconds ?? Math.max(0, data.stopAfterSeconds - 20 * 60),
              stopAfterSeconds: data.stopAfterSeconds,
              warningMessage: data.warningMessage ?? "",
            }
          : null,
    };
  } catch (err) {
    if (err instanceof z.ZodError) {
      return { ok: false, error: "BILLING_ERROR", message: "Unexpected billing response. Please try again." };
    }
    if (err instanceof ApiClientError) {
      const body = (err.raw as { response?: { data?: unknown } } | undefined)?.response?.data;
      return {
        ok: false,
        error: err.code || "BILLING_ERROR",
        message: err.message,
        // B20: a one-time FREE_MINUTES_USED says so (resetMode; B43 reason).
        details: billingErrorDetails(body),
        challenge: authRequiredDetails(body),
      };
    }
    return {
      ok: false,
      error: "BILLING_ERROR",
      message: err instanceof Error ? err.message : "Could not authorize billing.",
    };
  }
}

const PricingSchema = z.object({
  currencyCode: z.string(),
  ratePerMin: z.number(),
});

export type BoardroomPricing = {
  currencyCode: string;
  ratePerMin: number;
};

/** Live cost estimate — same `/billing/pricing` source mobile BillingContext uses. */
export async function fetchBoardroomPricing(): Promise<BoardroomPricing> {
  const raw = await apiGet<unknown>(billingEndpoints.pricing);
  return PricingSchema.parse(raw);
}

/**
 * Give back the card hold of a recording that was DISCARDED (02-10-2026).
 * Without this the hold stayed on the customer's card until the bank expired
 * it, days later, for a meeting they threw away. Best effort: a failure only
 * means the bank lets it go on its own, so it never blocks the discard.
 */
export async function releaseBoardroomHold(paymentIntentId: string | null | undefined): Promise<void> {
  if (!paymentIntentId) return;
  try {
    await apiPost(billingEndpoints.preauthorizeRelease, { paymentIntentId });
  } catch (err) {
    console.warn("[billingPreauth] Hold release failed (it will expire):", err);
  }
}
